import { Button as Kobalte } from "@kobalte/core/button"
import { type ComponentProps, Show, splitProps } from "solid-js"
import { Icon, IconProps } from "./icon"

type ShadcnButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
type LegacyButtonVariant = "primary"
type ShadcnButtonSize = "default" | "xs" | "sm" | "lg" | "icon" | "icon-xs" | "icon-sm" | "icon-lg"
type LegacyButtonSize = "small" | "normal" | "large"
type ButtonIconPlacement = "inline-start" | "inline-end"

export interface ButtonProps
  extends ComponentProps<typeof Kobalte>,
    Pick<ComponentProps<"button">, "class" | "classList" | "children"> {
  size?: ShadcnButtonSize | LegacyButtonSize
  variant?: ShadcnButtonVariant | LegacyButtonVariant
  icon?: IconProps["name"]
  iconPlacement?: ButtonIconPlacement
  asChild?: boolean
  /**
   * Let the button give up width when its row runs out, and ellipsize its label
   * instead of overflowing. Buttons are `flex-shrink: 0; white-space: nowrap`
   * by default — correct for a fixed label, wrong for one that carries data
   * (a model name, a chat title, a path). Without this, a `min-w-0` wrapper is
   * inert and the button paints over its neighbours in a narrow window.
   */
  truncate?: boolean
}

function variant(value?: ButtonProps["variant"]) {
  if (value === "primary") return "default"
  return value ?? "default"
}

function size(value?: ButtonProps["size"]) {
  if (value === "small") return "sm"
  if (value === "normal") return "default"
  if (value === "large") return "lg"
  return value ?? "default"
}

export function Button(props: ButtonProps) {
  const [split, rest] = splitProps(props, [
    "variant",
    "size",
    "icon",
    "iconPlacement",
    "asChild",
    "truncate",
    "class",
    "classList",
    "children",
  ])
  const iconPlacement = () => split.iconPlacement ?? "inline-start"
  return (
    <Kobalte
      {...rest}
      data-component="button"
      data-size={size(split.size)}
      data-variant={variant(split.variant)}
      data-icon={split.icon ? iconPlacement() : undefined}
      data-truncate={split.truncate ? "" : undefined}
      classList={{
        ...split.classList,
        [split.class ?? ""]: !!split.class,
      }}
    >
      <Show when={split.icon && iconPlacement() === "inline-start"}>
        <Icon name={split.icon!} size="small" data-icon="inline-start" />
      </Show>
      {/* A truncating button ellipsizes its label through a child element —
          a bare text node has nothing to hang `text-overflow` on and would
          clip mid-glyph. Wrap it so the contract holds whatever the call site
          passes. */}
      <Show when={split.truncate} fallback={split.children}>
        <span data-slot="button-label">{split.children}</span>
      </Show>
      <Show when={split.icon && iconPlacement() === "inline-end"}>
        <Icon name={split.icon!} size="small" data-icon="inline-end" />
      </Show>
    </Kobalte>
  )
}
